// Edit an existing StackSet: 4-step wizard mirroring StackSetCreate but
// pre-filled from the deployment, PUTting only the CHANGED fields
// (UpdateStackSetRequest semantics: nil/empty = keep). Names are immutable on
// AWS StackSets, so there is no Name step.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel, FormGroup,
  Grid, Paper, Radio, RadioGroup, Step, StepLabel, Stepper, TextField, Typography,
} from '@mui/material';
import LayersIcon from '@mui/icons-material/Layers';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, sectionCardSx, insetPanelSx, sectionHeadingSx, statusChipSx, actionRowSx,
} from '../portalStyles';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OrgTreeCheckbox from '../../../components/portal/OrgTreeCheckbox';
import MultiSelectField from '../securityhub/MultiSelectField';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import {
  getStackSet, getStackSetsOrgTree, getStackSetTemplate, listInstances, listOperations,
  updateStackSet, validateTemplate,
} from '../../../services/stacksetsApi';
import { collectAccounts } from './StackSetCreate';
import TemplateDiffView from './TemplateDiffView';

const STEPS = ['Template', 'Targets', 'Options', 'Review'];
const MAX_TEMPLATE_BYTES = 460800;
const ACTIVE = ['in_progress', 'updating', 'deleting'];

// One measure for every wizard step body so the form does not re-flow its
// width as the user advances (shared with StackSetCreate / StackSetsSetup).
const STEP_MAX_WIDTH = 720;

// Admin-console stepper: Metropolis labels, accent for the active step.
const stepperSx = {
  mb: 3,
  overflowX: 'auto',
  '& .MuiStepLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 500, color: CK.body },
  '& .MuiStepLabel-label.Mui-active': { fontWeight: 600, color: CK.navy },
  '& .MuiStepLabel-label.Mui-completed': { fontWeight: 600, color: CK.navy },
  '& .MuiStepIcon-root.Mui-active': { color: CK.blue },
  '& .MuiStepIcon-root.Mui-completed': { color: CK.green },
};

const parse = (raw, fallback) => { try { return JSON.parse(raw || ''); } catch { return fallback; } };

// Order-insensitive equality for string lists (regions, OU ids).
const sameSet = (a, b) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
};

// Canonical key for a {paramKey: value} map so maps compare order-insensitively.
const paramsKey = (m) => JSON.stringify(Object.entries(m).sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0)));

export default function StackSetEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(null); // {dep, template, params, ous, regions, prefs, cells}
  const [busyReason, setBusyReason] = useState(null);

  const [templateBody, setTemplateBody] = useState('');
  const [refreshFromURL, setRefreshFromURL] = useState(true); // URL-sourced: pull latest by default
  const [validating, setValidating] = useState(false);
  const [summary, setSummary] = useState(null); // set only after re-validating a CHANGED template
  const [paramValues, setParamValues] = useState({});
  const [capsAccepted, setCapsAccepted] = useState(false);
  const [orgTree, setOrgTree] = useState(null);
  const [selectedOUs, setSelectedOUs] = useState([]); // [{id,name,...}]
  const [regions, setRegions] = useState([]);
  const [failureTolerance, setFailureTolerance] = useState(10);
  const [maxConcurrent, setMaxConcurrent] = useState(100);
  const [retainRemoved, setRetainRemoved] = useState('delete');
  const [submitting, setSubmitting] = useState(false);

  // Load the deployment, its current template, instances (for the blast-radius
  // preview) and operations (busy gate), then pre-fill every wizard field.
  // Extracted into a callback so the pre-load error branch's Retry button can
  // re-run it.
  const load = useCallback(async () => {
    setError(null);
    setBusyReason(null);
    try {
      const [d, t, i, o] = await Promise.all([
        getStackSet(id), getStackSetTemplate(id), listInstances(id), listOperations(id),
      ]);
      const dep = d?.data;
      if (!dep) throw new Error('StackSet not found');
      const ops = o?.data || [];
      if (dep.status === 'deleted') {
        setBusyReason('This StackSet has been deleted and can no longer be updated.');
      } else if (ACTIVE.includes(dep.status) || ops.some((op) => op.status === 'running')) {
        setBusyReason('This StackSet has an operation in progress — wait for it to finish before editing.');
      }
      const template = t?.data?.templateBody || '';
      const params = parse(dep.parameters, []);
      const ous = parse(dep.targetOus, []);
      const regs = parse(dep.regions, []);
      const prefs = parse(dep.operationPrefs, {});
      setLoaded({
        dep,
        template,
        params,
        ous,
        regions: regs,
        prefs,
        // Live cells only — deleted/skipped rows are not stack instances.
        cells: (i?.data || [])
          .filter((r) => r.phase !== 'deleted' && r.phase !== 'skipped')
          .map((r) => `${r.accountId}|${r.region}`),
      });
      setTemplateBody(template);
      const pv = {};
      params.forEach((p) => { pv[p.key] = p.value; });
      setParamValues(pv);
      setSelectedOUs(ous.map((ou) => ({ id: ou.id, name: ou.name, type: 'OU' })));
      setRegions(regs);
      if (prefs.failureTolerancePercentage != null) setFailureTolerance(prefs.failureTolerancePercentage);
      if (prefs.maxConcurrentPercentage != null) setMaxConcurrent(prefs.maxConcurrentPercentage);
    } catch (e) {
      setError(e.message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        // Same raw-vs-envelope handling as StackSetCreate.
        const res = await getStackSetsOrgTree();
        setOrgTree(res?.tree ? res : res?.data || null);
      } catch (e) {
        setError(`Could not load the organization hierarchy: ${e.message}`);
      }
    })();
  }, []);

  const previewAccounts = useMemo(() => {
    if (!orgTree?.tree) return [];
    return [...collectAccounts(orgTree.tree, selectedOUs.map((o) => o.id))];
  }, [orgTree, selectedOUs]);

  // Error and loading keep the page header mounted so only the card body under
  // it changes when the StackSet lands.
  if (error && !loaded) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title="Update StackSet" subtitle="CloudFormation StackSet" />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={error} onRetry={load} />
        </motion.div>
      </motion.div>
    );
  }
  if (!loaded) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title="Update StackSet" subtitle="CloudFormation StackSet" />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading StackSet…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (busyReason) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          <PortalPageHeader icon={<LayersIcon sx={{ fontSize: 24 }} />} title={`Update ${loaded.dep.name}`} subtitle="CloudFormation StackSet" />
        </motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>{busyReason}</Alert>
            <Button variant="outlined" onClick={() => navigate(`/stacksets/${id}`)}>Back to StackSet</Button>
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  // --- changed-field detection (template compared as strings — no SHA client-side) ---
  // URL-sourced StackSets read their template from S3 on each deploy; "changed"
  // is the user's intent to pull the latest (the backend decides if the content
  // actually differs and no-ops otherwise).
  const isURLSourced = !!loaded.dep.templateSourceUrl;
  const templateChanged = isURLSourced ? refreshFromURL : (templateBody !== loaded.template);
  const storedParams = {};
  loaded.params.forEach((p) => { storedParams[p.key] = p.value; });
  const paramsChanged = paramsKey(paramValues) !== paramsKey(storedParams);
  const ousChanged = !sameSet(selectedOUs.map((o) => o.id), loaded.ous.map((o) => o.id));
  const regionsChanged = !sameSet(regions, loaded.regions);
  const prefsChanged = Number(failureTolerance) !== Number(loaded.prefs.failureTolerancePercentage ?? 10)
    || Number(maxConcurrent) !== Number(loaded.prefs.maxConcurrentPercentage ?? 100);
  const targetsChanged = ousChanged || regionsChanged;
  const nothingChanged = !templateChanged && !paramsChanged && !targetsChanged && !prefsChanged;

  // Blast-radius preview vs the CURRENT instances (backend re-diffs authoritatively).
  const instanceCount = loaded.cells.length;
  let addCount = 0;
  let removeCount = 0;
  if (targetsChanged && orgTree?.tree) {
    const desired = new Set();
    previewAccounts.forEach((a) => regions.forEach((r) => desired.add(`${a}|${r}`)));
    const current = new Set(loaded.cells);
    desired.forEach((c) => { if (!current.has(c)) addCount += 1; });
    current.forEach((c) => { if (!desired.has(c)) removeCount += 1; });
  }

  const capsRequired = templateChanged && (summary?.capabilities || []).length > 0;

  // Every template-body change funnels through here. Validation overwrites
  // paramValues with the NEW template's keys, so if the body reverts to the
  // deployed template the params must snap back to the stored values —
  // otherwise stale new-template keys would be submitted as "changed" params
  // against the old template.
  const changeTemplate = (body) => {
    setTemplateBody(body);
    setSummary(null);
    if (body === loaded.template) setParamValues({ ...storedParams });
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_TEMPLATE_BYTES) {
      setError(`Template exceeds the ${Math.floor(MAX_TEMPLATE_BYTES / 1024)}KB limit`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { changeTemplate(String(reader.result)); setError(null); };
    reader.onerror = () => setError('Could not read the selected file');
    reader.readAsText(file);
    // Reset the input so re-selecting the same file fires onChange again
    // (readAsText has already captured the file; clearing is safe here).
    e.target.value = '';
  };

  // Re-validate only when the template body changed; merge the new template's
  // parameters with the stored values (stored value wins, else the default).
  const runValidate = async () => {
    setValidating(true);
    setError(null);
    try {
      const res = await validateTemplate(
        isURLSourced ? '' : templateBody,
        isURLSourced ? loaded.dep.templateSourceUrl : '',
      );
      const s = res?.data || {};
      setSummary(s);
      const next = {};
      (s.parameters || []).forEach((p) => {
        next[p.key] = storedParams[p.key] !== undefined ? storedParams[p.key] : (p.defaultValue || '');
      });
      setParamValues(next);
      setCapsAccepted(!(s.capabilities || []).length);
      setActiveStep(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setValidating(false);
    }
  };

  // Only send changed fields — nil means "keep" on the backend.
  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const body = {};
      if (isURLSourced) {
        if (refreshFromURL) {
          body.refreshFromSourceUrl = true; // backend re-reads the URL & redeploys if changed
          if (summary) body.capabilities = summary.capabilities || [];
        }
      } else if (templateChanged) {
        body.templateBody = templateBody;
        // The re-validation summary carries the NEW template's capability set.
        body.capabilities = summary?.capabilities || [];
      }
      if (paramsChanged || templateChanged) {
        // noEcho travels with each parameter so the Detail page can mask it:
        // from the re-validation summary when the template changed, else from
        // the stored parameters. A template change always re-sends parameters
        // — the new summary's noEcho flags are authoritative even when keys
        // and values are identical.
        const noEchoFor = (key) => (summary
          ? !!(summary.parameters || []).find((p) => p.key === key)?.noEcho
          : !!loaded.params.find((p) => p.key === key)?.noEcho);
        body.parameters = Object.entries(paramValues).map(([key, value]) => ({ key, value, noEcho: noEchoFor(key) }));
      }
      if (ousChanged) body.targetOus = selectedOUs.map((o) => ({ id: o.id, name: o.name }));
      if (regionsChanged) body.regions = regions;
      if (prefsChanged) {
        body.operationPrefs = {
          failureTolerancePercentage: Number(failureTolerance),
          maxConcurrentPercentage: Number(maxConcurrent),
          regionConcurrencyType: 'PARALLEL',
        };
      }
      body.retainStacks = retainRemoved === 'retain'; // applies to instances removed by target shrink
      await updateStackSet(id, body);
      navigate(`/stacksets/${id}`);
    } catch (e) {
      setError(e.message);
      setSubmitting(false);
    }
  };

  // Parameters form: the new template's summary when re-validated, else the
  // stored key/value pairs (no re-validation needed for an unchanged template).
  const paramFields = summary
    ? (summary.parameters || [])
    : loaded.params.map((p) => ({ key: p.key, description: '', noEcho: !!p.noEcho }));

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>
        <PortalPageHeader
          icon={<LayersIcon sx={{ fontSize: 24 }} />}
          title={`Update ${loaded.dep.name}`}
          subtitle="StackSet names are immutable — everything else can change"
        />
      </motion.div>
      {error && (
        <motion.div variants={sectionVariants}>
          <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>{error}</Alert>
        </motion.div>
      )}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={sectionCardSx}>
          <Stepper activeStep={activeStep} sx={stepperSx}>
            {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
          </Stepper>

          {activeStep === 0 && isURLSourced && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <TextField
                label="Template S3 URL" value={loaded.dep.templateSourceUrl}
                InputProps={{ readOnly: true }}
                helperText="This StackSet reads its template from S3 on every deploy. Update the file at this URL, then pull the latest below to roll it out."
              />
              <FormControlLabel
                control={<Checkbox checked={refreshFromURL} onChange={(e) => { setRefreshFromURL(e.target.checked); setSummary(null); }} />}
                label="Pull the latest template from this S3 URL and redeploy if it changed"
              />
              <Box sx={actionRowSx}>
                <Button onClick={() => navigate(`/stacksets/${id}`)}>Cancel</Button>
                {refreshFromURL ? (
                  <Button variant="contained" disabled={validating} onClick={runValidate}>
                    {validating ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Validate latest & continue'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => setActiveStep(1)}>Next</Button>
                )}
              </Box>
            </Box>
          )}

          {activeStep === 0 && !isURLSourced && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              {!templateChanged && (
                <Alert severity="info" sx={{ borderRadius: '8px' }}>
                  Template unchanged — parameters keep their stored values and no
                  capability re-acknowledgement is needed.
                </Alert>
              )}
              <Button variant="outlined" component="label" sx={{ alignSelf: 'flex-start' }}>
                Upload template file (.json / .yaml)
                <input hidden type="file" accept=".json,.yaml,.yml,.template" onChange={onFile} />
              </Button>
              <TextField
                label="Template body" value={templateBody}
                onChange={(e) => changeTemplate(e.target.value)}
                multiline rows={14} sx={{ fontFamily: 'monospace' }}
              />
              <Box sx={actionRowSx}>
                <Button onClick={() => navigate(`/stacksets/${id}`)}>Cancel</Button>
                {templateChanged ? (
                  <Button variant="contained" disabled={!templateBody.trim() || validating} onClick={runValidate}>
                    {validating ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : 'Validate & continue'}
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => setActiveStep(1)}>Next</Button>
                )}
              </Box>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              {paramFields.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Template parameters</Typography>
                  <Grid container spacing={2}>
                    {paramFields.map((p) => (
                      <Grid key={p.key} size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          label={p.key} helperText={p.description} type={p.noEcho ? 'password' : 'text'}
                          value={paramValues[p.key] ?? ''} onChange={(e) => setParamValues({ ...paramValues, [p.key]: e.target.value })}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Target OUs</Typography>
              {!orgTree?.tree ? <PortalLoading label="Loading the organization hierarchy…" py={4} /> : (
                <Paper elevation={0} sx={{ ...insetPanelSx, p: 1, maxHeight: 320, overflow: 'auto', mb: 2 }}>
                  {orgTree.tree.map((n) => (
                    <OrgTreeCheckbox
                      key={n.id} node={n} selectedIds={selectedOUs}
                      onToggle={(sel) => {
                        if (sel.type === 'ACCOUNT') return; // OUs (and tenant root) only
                        setSelectedOUs((cur) => (cur.some((c) => c.id === sel.id)
                          ? cur.filter((c) => c.id !== sel.id) : [...cur, sel]));
                      }}
                    />
                  ))}
                </Paper>
              )}
              <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                {previewAccounts.length} account{previewAccounts.length === 1 ? '' : 's'} will receive stacks
                {regions.length > 0 ? ` in ${regions.length} region${regions.length === 1 ? '' : 's'} (${previewAccounts.length * regions.length} stack instances)` : ''}.
              </Alert>
              <MultiSelectField
                label="Regions" options={AWS_REGIONS} value={regions} onChange={setRegions}
              />
              <Box sx={{ ...actionRowSx, mt: 2 }}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button variant="contained" disabled={selectedOUs.length === 0 || regions.length === 0} onClick={() => setActiveStep(2)}>Next</Button>
              </Box>
            </Box>
          )}

          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: STEP_MAX_WIDTH }}>
              <TextField
                label="Failure tolerance (%)" type="number" value={failureTolerance}
                onChange={(e) => setFailureTolerance(e.target.value)}
                helperText="Percentage of accounts per region that can fail before the operation stops"
              />
              <TextField
                label="Max concurrent accounts (%)" type="number" value={maxConcurrent}
                onChange={(e) => setMaxConcurrent(e.target.value)}
              />
              {capsRequired && (
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={capsAccepted} onChange={(e) => setCapsAccepted(e.target.checked)} />}
                    label={`I acknowledge that this template requires: ${(summary.capabilities || []).join(', ')}`}
                  />
                </FormGroup>
              )}
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(1)}>Back</Button>
                <Button variant="contained" disabled={capsRequired && !capsAccepted} onClick={() => setActiveStep(3)}>Next</Button>
              </Box>
            </Box>
          )}

          {activeStep === 3 && (
            <Box sx={{ maxWidth: STEP_MAX_WIDTH }}>
              <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Review</Typography>
              <Box sx={{ ...insetPanelSx, display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Chip size="small" label={`Name: ${loaded.dep.name}`} sx={statusChipSx('neutral')} />
                <Chip size="small" label={`Template: ${templateChanged ? `changes to v${loaded.dep.templateVersion + 1}` : `v${loaded.dep.templateVersion} (unchanged)`}`} sx={statusChipSx(templateChanged ? 'warning' : 'neutral')} />
                <Chip size="small" label={`Parameters: ${paramsChanged ? 'changed' : 'unchanged'}`} sx={statusChipSx(paramsChanged ? 'warning' : 'neutral')} />
                <Chip size="small" label={`Targets: ${targetsChanged ? 'changed' : 'unchanged'}`} sx={statusChipSx(targetsChanged ? 'warning' : 'neutral')} />
                <Chip size="small" label={`Preferences: ${prefsChanged ? 'changed' : 'unchanged'}`} sx={statusChipSx(prefsChanged ? 'warning' : 'neutral')} />
              </Box>
              {/* Template change preview — the StackSet stand-in for a change set.
                  Body-sourced: diff the deployed template against the edited one.
                  URL-sourced: no client-side "before" (the URL is read live), so
                  point at the per-instance CloudFormation events instead. */}
              {templateChanged && !isURLSourced && (
                <TemplateDiffView oldText={loaded.template} newText={templateBody} />
              )}
              {templateChanged && isURLSourced && (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                  Pulling the latest template from S3. CloudFormation computes the exact
                  per-account resource changes on deploy — watch each instance&apos;s live
                  events on the StackSet page.
                </Alert>
              )}
              {nothingChanged ? (
                <Alert severity="info" sx={{ mb: 2, borderRadius: '8px' }}>
                  Nothing has changed — there is no update to submit.
                </Alert>
              ) : (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                  Updating a StackSet redeploys to EVERY existing stack instance
                  ({instanceCount} instances) — plus {addCount} new and −{removeCount} removed
                  from target changes.
                </Alert>
              )}
              {targetsChanged && removeCount > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>
                    Removed targets ({removeCount} instance{removeCount === 1 ? '' : 's'})
                  </Typography>
                  <RadioGroup value={retainRemoved} onChange={(e) => setRetainRemoved(e.target.value)}>
                    <FormControlLabel value="delete" control={<Radio />} label="Delete the removed accounts' stacks — destroys their resources" />
                    <FormControlLabel value="retain" control={<Radio />} label="Retain the removed accounts' stacks — resources stay (and keep billing)" />
                  </RadioGroup>
                </Box>
              )}
              <Box sx={actionRowSx}>
                <Button onClick={() => setActiveStep(2)}>Back</Button>
                <Button variant="contained" disabled={submitting || nothingChanged} onClick={submit}>
                  {submitting ? 'Updating…' : 'Update StackSet'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </motion.div>
    </motion.div>
  );
}
